/**
 * OwO! Identity audit vocabulary.
 * Labels are written for operators scanning a control plane, not raw event bus names.
 * Severity encodes how loudly the event should interrupt that scan.
 */

export type AuditSeverity = "critical" | "failure" | "warning" | "success" | "info";

export type AuditOutcome = "success" | "failure";

export interface AuditActionMeta {
  label: string;
  labelEn: string;
  /** How urgent this event is for an identity operator. */
  severity: AuditSeverity;
}

const ACTIONS: Record<string, AuditActionMeta> = {
  "user.registered": { label: "用户注册", labelEn: "User registered", severity: "success" },
  "user.admin_created": { label: "管理员添加用户", labelEn: "User added by administrator", severity: "warning" },
  "user.deactivated": { label: "停用用户", labelEn: "User deactivated", severity: "warning" },
  "user.deleted": { label: "永久删除用户", labelEn: "User permanently deleted", severity: "critical" },
  "user.invitation_created": { label: "发出邀请", labelEn: "Invitation created", severity: "info" },
  "user.invitation_accepted": { label: "接受邀请", labelEn: "Invitation accepted", severity: "success" },
  "user.invitation_revoked": { label: "撤销邀请", labelEn: "Invitation revoked", severity: "warning" },
  "tenant.registration_updated": { label: "更新自行注册", labelEn: "Self-registration updated", severity: "warning" },
  "tenant.security_policy_updated": { label: "更新安全策略", labelEn: "Security policy updated", severity: "warning" },
  "developer.application_submitted": { label: "提交开发者申请", labelEn: "Developer application submitted", severity: "info" },
  "developer.application_approved": { label: "通过开发者申请", labelEn: "Developer application approved", severity: "success" },
  "developer.application_rejected": { label: "拒绝开发者申请", labelEn: "Developer application rejected", severity: "warning" },
  "developer.role_revoked": { label: "收回开发者权限", labelEn: "Developer role revoked", severity: "warning" },
  "oauth.client_secret_rotated": { label: "轮换 Client Secret", labelEn: "Client secret rotated", severity: "warning" },
  "oauth.client_previous_secret_retired": { label: "退役旧 Client Secret", labelEn: "Previous client secret retired", severity: "info" },
  "rbac.role_created": { label: "创建角色", labelEn: "Role created", severity: "info" },
  "rbac.role_updated": { label: "更新角色", labelEn: "Role updated", severity: "info" },
  "rbac.role_deleted": { label: "删除角色", labelEn: "Role deleted", severity: "warning" },
  "rbac.role_assigned": { label: "分配角色", labelEn: "Role assigned", severity: "warning" },
  "rbac.role_unassigned": { label: "移除角色", labelEn: "Role unassigned", severity: "warning" },
  "rbac.role_permissions_replaced": { label: "替换角色权限", labelEn: "Role permissions replaced", severity: "warning" },
  "rbac.role_permission_assigned": { label: "授予权限", labelEn: "Permission assigned", severity: "warning" },
  "rbac.role_permission_unassigned": { label: "收回权限", labelEn: "Permission unassigned", severity: "warning" },
  "rbac.group_created": { label: "创建用户组", labelEn: "Group created", severity: "info" },
  "rbac.group_updated": { label: "更新用户组", labelEn: "Group updated", severity: "info" },
  "rbac.group_deleted": { label: "删除用户组", labelEn: "Group deleted", severity: "warning" },
  "rbac.group_member_added": { label: "加入组成员", labelEn: "Group member added", severity: "info" },
  "rbac.group_member_removed": { label: "移除组成员", labelEn: "Group member removed", severity: "warning" },
  "rbac.group_role_assigned": { label: "绑定组角色", labelEn: "Group role assigned", severity: "warning" },
  "rbac.group_role_unassigned": { label: "解绑组角色", labelEn: "Group role unassigned", severity: "warning" },
  "rbac.super_admin_granted": { label: "授予超级管理员", labelEn: "Super admin granted", severity: "critical" },
  "rbac.super_admin_revoked": { label: "撤销超级管理员", labelEn: "Super admin revoked", severity: "critical" },
  "user.bulk_activate": { label: "批量启用用户", labelEn: "Users bulk-activated", severity: "warning" },
  "user.bulk_deactivate": { label: "批量停用用户", labelEn: "Users bulk-deactivated", severity: "warning" },
  "access_review.created": { label: "创建访问评审", labelEn: "Access review created", severity: "info" },
  "access_review.completed": { label: "完成访问评审", labelEn: "Access review completed", severity: "success" },
  "access_review.item_attested": { label: "确认保留访问", labelEn: "Access attested", severity: "info" },
  "access_review.item_revoked": { label: "评审撤销访问", labelEn: "Access revoked in review", severity: "warning" },
  "scim.user_created": { label: "SCIM 创建用户", labelEn: "SCIM user created", severity: "info" },
  "scim.user_replaced": { label: "SCIM 替换用户", labelEn: "SCIM user replaced", severity: "info" },
  "scim.user_patched": { label: "SCIM 更新用户", labelEn: "SCIM user patched", severity: "info" },
  "scim.user_deleted": { label: "SCIM 删除用户", labelEn: "SCIM user deleted", severity: "warning" },
  "scim.group_created": { label: "SCIM 创建组", labelEn: "SCIM group created", severity: "info" },
  "scim.group_replaced": { label: "SCIM 替换组", labelEn: "SCIM group replaced", severity: "info" },
  "scim.group_patched": { label: "SCIM 更新组", labelEn: "SCIM group patched", severity: "info" },
  "scim.group_deleted": { label: "SCIM 删除组", labelEn: "SCIM group deleted", severity: "warning" },
  "auth.login_success": { label: "登录成功", labelEn: "Login succeeded", severity: "success" },
  "auth.login_failed": { label: "登录失败", labelEn: "Login failed", severity: "failure" },
  "auth.logout": { label: "退出登录", labelEn: "Logged out", severity: "info" },
  "security.token_reuse_detected": { label: "检测到令牌重用", labelEn: "Token reuse detected", severity: "critical" },
  "security.risk_step_up": { label: "要求额外验证", labelEn: "Step-up verification required", severity: "warning" },
  "audit.archive_downloaded": { label: "下载审计归档", labelEn: "Audit archive downloaded", severity: "warning" },
  "security.signing_key_rotated": { label: "轮换签名密钥", labelEn: "Signing key rotated", severity: "warning" },
  "security.signing_key_retired": { label: "退役签名密钥", labelEn: "Signing key retired", severity: "warning" },
  "mfa.enabled": { label: "启用多因素认证", labelEn: "MFA enabled", severity: "success" },
  "mfa.disabled": { label: "停用多因素认证", labelEn: "MFA disabled", severity: "warning" },
  "user.password_changed": { label: "修改密码", labelEn: "Password changed", severity: "warning" },
  "user.password_reset_by_admin": { label: "管理员重置密码", labelEn: "Password reset by admin", severity: "warning" },
  "user.password_reset": { label: "重置密码", labelEn: "Password reset", severity: "warning" },
  "user.email_verified": { label: "邮箱已验证", labelEn: "Email verified", severity: "success" },
  "user.email_verification_failed": { label: "邮箱验证失败", labelEn: "Email verification failed", severity: "failure" },
  "payment.bill_created": { label: "账单已创建", labelEn: "Bill created", severity: "info" },
  "payment.bill_paid": { label: "账单已支付", labelEn: "Bill paid", severity: "success" },
  "payment.bill_cancelled": { label: "账单已取消", labelEn: "Bill cancelled", severity: "info" },
  "payment.bill_timed_out": { label: "账单已超时", labelEn: "Bill timed out", severity: "failure" },
  "payment.bill_refunded": { label: "账单已退款", labelEn: "Bill refunded", severity: "info" },
  "payment.invoice_uploaded": { label: "发票已上传", labelEn: "Invoice uploaded", severity: "info" },
  "payment.withdrawal_submitted": { label: "提现申请已提交", labelEn: "Withdrawal submitted", severity: "info" },
  "payment.withdrawal_approved": { label: "提现申请已批准", labelEn: "Withdrawal approved", severity: "success" },
  "payment.withdrawal_rejected": { label: "提现申请已拒绝", labelEn: "Withdrawal rejected", severity: "failure" },
  "payment.withdrawal_processed": { label: "提现已打款", labelEn: "Withdrawal processed", severity: "success" },
  "payment.stripe_config_updated": { label: "Stripe 配置已更新", labelEn: "Stripe config updated", severity: "info" },
  "portal.session_revoked": { label: "撤销登录会话", labelEn: "Session revoked", severity: "warning" },
  "portal.sessions_revoked": { label: "批量撤销会话", labelEn: "Sessions revoked", severity: "warning" },
  "user.password_reset_failed": { label: "密码重置失败", labelEn: "Password reset failed", severity: "failure" },
  "user.banned": { label: "封禁用户", labelEn: "User banned", severity: "critical" },
  "rate_limit_exceeded": { label: "触发速率限制", labelEn: "Rate limit exceeded", severity: "critical" },
  "security.ip_rule_blocked": { label: "IP 规则拦截", labelEn: "Blocked by IP rule", severity: "critical" },
  "saml.sso_success": { label: "SAML 登录成功", labelEn: "SAML SSO succeeded", severity: "success" },
  "saml.sso_failed": { label: "SAML 登录失败", labelEn: "SAML SSO failed", severity: "failure" },
  "saml.slo_success": { label: "SAML 注销成功", labelEn: "SAML SLO succeeded", severity: "info" },
  "saml.slo_failed": { label: "SAML 注销失败", labelEn: "SAML SLO failed", severity: "failure" },
  "saml.sp_cert_rotated": { label: "轮换 SAML 证书", labelEn: "SAML SP cert rotated", severity: "warning" },
  "saml.sp_previous_cert_retired": { label: "退役旧 SAML 证书", labelEn: "Previous SAML cert retired", severity: "info" },
  "ciba.notify_failed": { label: "后台登录通知失败", labelEn: "Backchannel login notification failed", severity: "failure" },
  "federation.login_success": { label: "联邦登录成功", labelEn: "Federation login succeeded", severity: "success" },
  "federation.login_failed": { label: "联邦登录失败", labelEn: "Federation login failed", severity: "failure" },
  "federation.provider_created": { label: "创建联邦身份源", labelEn: "Federation provider created", severity: "info" },
  "federation.provider_updated": { label: "更新联邦身份源", labelEn: "Federation provider updated", severity: "info" },
  "federation.provider_deleted": { label: "删除联邦身份源", labelEn: "Federation provider deleted", severity: "warning" },
  "federation.link_success": { label: "关联联邦账号", labelEn: "Federation account linked", severity: "success" },
  "federation.link_failed": { label: "关联联邦账号失败", labelEn: "Federation link failed", severity: "failure" },
  "federation.unlink_success": { label: "解除联邦账号", labelEn: "Federation account unlinked", severity: "warning" },
  "federation.unlink_failed": { label: "解除联邦账号失败", labelEn: "Federation unlink failed", severity: "failure" },
  "mail.email_verification": { label: "发送邮箱验证", labelEn: "Verification email sent", severity: "info" },
  "mail.password_reset": { label: "发送重置密码邮件", labelEn: "Password reset email sent", severity: "info" },
  "mail.invitation": { label: "发送邀请邮件", labelEn: "Invitation email sent", severity: "info" },
  "mail.security_alert": { label: "发送安全提醒", labelEn: "Security alert sent", severity: "warning" },
  "mail.template_updated": { label: "更新发信模板", labelEn: "Mail template updated", severity: "warning" },
  "smtp.probe": { label: "测试 SMTP", labelEn: "SMTP probe", severity: "info" },
  "smtp.delivery_failed": { label: "邮件发送失败", labelEn: "Mail delivery failed", severity: "failure" },
  "platform.domains_updated": { label: "更新域名边界", labelEn: "Domain boundary updated", severity: "warning" },
  "platform.runtime_updated": { label: "更新运行配置", labelEn: "Runtime settings updated", severity: "warning" },
  "platform.user_dossier_viewed": { label: "查看用户档案", labelEn: "User dossier viewed", severity: "info" },
  "platform.asset_mutated": { label: "修改平台资产", labelEn: "Platform asset changed", severity: "warning" },
  "platform.asset_deleted": { label: "删除平台资产", labelEn: "Platform asset deleted", severity: "critical" },
  "platform.cache_purged": { label: "清理运行缓存", labelEn: "Runtime cache purged", severity: "warning" },
  "platform.logs_purged": { label: "清理运行日志", labelEn: "Runtime logs purged", severity: "warning" },
  "platform.audit_purged": { label: "清理审计日志", labelEn: "Audit log purged", severity: "critical" },
  "platform.logo_policy_updated": { label: "更新 Logo 上限", labelEn: "Logo limit updated", severity: "warning" },
  "platform.backup_policy_updated": { label: "更新平台备份设置", labelEn: "Platform backup settings updated", severity: "warning" },
  "platform.backup_started": { label: "开始平台备份", labelEn: "Platform backup started", severity: "info" },
  "platform.backup_completed": { label: "完成平台备份", labelEn: "Platform backup completed", severity: "success" },
  "platform.backup_failed": { label: "平台备份失败", labelEn: "Platform backup failed", severity: "failure" },
  "platform.backup_expired": { label: "删除过期备份", labelEn: "Platform backup deleted", severity: "warning" },
  "platform.backup_downloaded": { label: "下载平台备份", labelEn: "Platform backup downloaded", severity: "critical" },
  "platform.backup_deleted": { label: "删除平台备份", labelEn: "Platform backup deleted", severity: "critical" },
  "platform.backup_digest": { label: "发送备份周期通知", labelEn: "Backup cycle notice sent", severity: "info" },
  "security.step_up_password_set": { label: "设置二级密码", labelEn: "Step-up password set", severity: "warning" },
  "security.step_up_password_cleared": { label: "清除二级密码", labelEn: "Step-up password cleared", severity: "warning" },
  "security.step_up_failed": { label: "二级确认失败", labelEn: "Step-up failed", severity: "failure" },
  "oauth.client_logo_updated": { label: "更新应用 Logo", labelEn: "Application logo updated", severity: "info" },
  "oauth.client_logo_choice": { label: "确定应用 Logo 优先级", labelEn: "Application logo preference saved", severity: "info" },
  "oauth.client_enabled": { label: "启用 OIDC 应用", labelEn: "OIDC application enabled", severity: "warning" },
  "oauth.client_disabled": { label: "停用 OIDC 应用", labelEn: "OIDC application paused", severity: "warning" },
  "oauth.client_owner_transferred": { label: "转移 OIDC 应用所有者", labelEn: "OIDC application owner transferred", severity: "warning" },
  "oauth.client.platform_verified": { label: "授予平台人工校验", labelEn: "Manual review granted", severity: "success" },
  "oauth.client.platform_verification_revoked": { label: "撤销平台人工校验", labelEn: "Manual review removed", severity: "warning" },
  "legal.document_created": { label: "新建条款草稿", labelEn: "Legal draft created", severity: "info" },
  "legal.document_revision_submitted": { label: "提交条款修改", labelEn: "Legal revision submitted", severity: "info" },
  "legal.document_published": { label: "发布条款", labelEn: "Legal document published", severity: "warning" },
  "support.ticket_replied": { label: "回复工单", labelEn: "Support ticket replied", severity: "info" },
  "support.ticket_feedback_requested": { label: "邀请工单反馈", labelEn: "Ticket feedback requested", severity: "info" },
  "platform.maintenance_changed": { label: "更新维护开关", labelEn: "Maintenance mode changed", severity: "warning" },
};

const SEVERITY_LABEL: Record<AuditSeverity, { zh: string; en: string; tag: string }> = {
  critical: { zh: "危急", en: "Critical", tag: "magenta" },
  failure: { zh: "失败", en: "Failure", tag: "error" },
  warning: { zh: "警告", en: "Warning", tag: "warning" },
  success: { zh: "成功", en: "Success", tag: "success" },
  info: { zh: "信息", en: "Info", tag: "processing" },
};

function failureByActionName(action: string): boolean {
  const name = action.toLowerCase();
  return (
    name.includes("failed")
    || name.includes("exceeded")
    || name.endsWith("_blocked")
    || name.includes("token_reuse")
  );
}

function inferMeta(action: string): AuditActionMeta {
  if (failureByActionName(action)) {
    return { label: action, labelEn: action, severity: action.includes("token_reuse") || action.includes("blocked") || action.includes("exceeded") ? "critical" : "failure" };
  }
  if (action.includes("banned") || action.includes("super_admin")) {
    return { label: action, labelEn: action, severity: "critical" };
  }
  if (action.includes("revoked") || action.includes("rotated") || action.includes("password") || action.includes("disabled") || action.includes("deleted")) {
    return { label: action, labelEn: action, severity: "warning" };
  }
  if (action.includes("success") || action.includes("enabled") || action.includes("verified") || action.includes("accepted") || action.includes("registered")) {
    return { label: action, labelEn: action, severity: "success" };
  }
  return { label: action, labelEn: action, severity: "info" };
}

export function auditActionMeta(action: unknown): AuditActionMeta {
  const key = String(action ?? "").trim();
  if (!key) return { label: "未知动作", labelEn: "Unknown action", severity: "info" };
  return ACTIONS[key] ?? inferMeta(key);
}

export function auditActionLabel(action: unknown, locale: "zh-CN" | "en" = "zh-CN"): string {
  const meta = auditActionMeta(action);
  return locale === "en" ? meta.labelEn : meta.label;
}

/**
 * Outcome answers “did the attempted operation succeed?”
 * Action name wins over a stale success column (historical GORM default bug).
 */
export function auditOutcome(action: unknown, successField: unknown): AuditOutcome {
  const key = String(action ?? "");
  if (failureByActionName(key)) return "failure";
  if (typeof successField === "boolean") return successField ? "success" : "failure";
  if (successField === "false" || successField === 0) return "failure";
  return "success";
}

export function auditSeverity(action: unknown, successField?: unknown): AuditSeverity {
  const meta = auditActionMeta(action);
  if (auditOutcome(action, successField) === "failure" && meta.severity !== "critical") {
    return "failure";
  }
  return meta.severity;
}

export function auditSeverityLabel(severity: AuditSeverity, locale: "zh-CN" | "en" = "zh-CN"): string {
  const entry = SEVERITY_LABEL[severity];
  return locale === "en" ? entry.en : entry.zh;
}

export function auditSeverityTagColor(severity: AuditSeverity): string {
  return SEVERITY_LABEL[severity].tag;
}

export function auditOutcomeLabel(outcome: AuditOutcome, locale: "zh-CN" | "en" = "zh-CN"): string {
  if (locale === "en") return outcome === "success" ? "Succeeded" : "Failed";
  return outcome === "success" ? "成功" : "失败";
}

export function auditOutcomeTagColor(outcome: AuditOutcome): string {
  return outcome === "success" ? "success" : "error";
}
